/**
 * SOVRANOVA — device-side audio SAFETY BUFFER (not an archive)
 * ------------------------------------------------------------
 * Decision of record (13 Aug 2026, David): audio is kept ONLY on this device,
 * ONLY until its transcription is confirmed saved — then it deletes itself.
 * This is the middle path that keeps the 28 Jul promise ("voice is never
 * stored") true while making the 13 Aug total-loss failure impossible: a clip
 * whose transcription fails survives a crash, a reload and a flat battery,
 * sitting in IndexedDB until it is recovered or explicitly discarded.
 *
 * NOTHING here ever leaves the device. There is no sync, no upload of raw
 * audio, no server-side counterpart. Deleting the app's site data deletes it.
 */

const DB_NAME = 'sovranova-audio-buffer'
const STORE = 'clips'

function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1)
    req.onupgradeneeded = () => {
      const db = req.result
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'id' })
      }
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
}

async function tx(mode, fn) {
  const db = await openDB()
  try {
    return await new Promise((resolve, reject) => {
      const t = db.transaction(STORE, mode)
      const store = t.objectStore(STORE)
      const out = fn(store)
      t.oncomplete = () => resolve(out?.result !== undefined ? out.result : undefined)
      t.onerror = () => reject(t.error)
      t.onabort = () => reject(t.error)
    })
  } finally { db.close() }
}

/** Save a clip BEFORE attempting transcription. context: 'debrief' | 'capture'. */
export async function bufferPut(id, blob, mimeType, context = 'debrief') {
  try {
    await tx('readwrite', (s) => s.put({ id, blob, mimeType, context, createdAt: Date.now() }))
    return true
  } catch { return false }   // buffer is best-effort — never blocks recording
}

/** The moment a clip's words are confirmed saved, its audio has done its job. */
export async function bufferDelete(id) {
  try { await tx('readwrite', (s) => s.delete(id)); return true } catch { return false }
}

/** Clips still waiting — i.e. audio whose words are NOT yet saved anywhere. */
export async function bufferList() {
  try {
    const db = await openDB()
    try {
      return await new Promise((resolve, reject) => {
        const t = db.transaction(STORE, 'readonly')
        const req = t.objectStore(STORE).getAll()
        req.onsuccess = () => resolve(req.result || [])
        req.onerror = () => reject(req.error)
      })
    } finally { db.close() }
  } catch { return [] }
}

export async function bufferClear() {
  try { await tx('readwrite', (s) => s.clear()); return true } catch { return false }
}

/** Stable id without Date.now() collisions across quick cycles. */
export function clipId() {
  return (crypto?.randomUUID?.() || `clip-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`)
}
